import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { downscaleImage } from '../../utils/imageDownscale';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Camera, Plus, Trash2, Image, Upload, GripVertical, Info, UserCircle
} from 'lucide-react';
import { toast } from 'sonner';

const CLOUDINARY_UPLOAD_PRESET = 'ml_default';

const TalentPortfolio = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [captionInput, setCaptionInput] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await api.get('/profile/portfolio');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (file) => {
    if (!file) return;
    file = await downscaleImage(file); // mobile camera photos are 6-15MB raw
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }

    setUploading(true);
    try {
      // Get Cloudinary signature
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'ieventur/portfolio',
        resource_type: 'image'
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'ieventur/portfolio');

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      const uploadData = await uploadRes.json();

      if (!uploadData.secure_url) throw new Error('Upload failed');

      // Save to backend
      await api.post('/profile/portfolio', {
        photo_url: uploadData.secure_url,
        caption: captionInput || null,
        photo_type: 'portfolio'
      });

      toast.success('Photo uploaded!');
      setCaptionInput('');
      setShowUploadDialog(false);
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await api.delete(`/profile/portfolio/${photoId}`);
      toast.success('Photo removed');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Iter-224c — Promote a portfolio photo to be the profile photo.
  // No re-upload needed.
  const handleSetAsProfile = async (photoId) => {
    try {
      const res = await api.post(`/profile/portfolio/${photoId}/set-as-profile`);
      toast.success('Profile photo updated.');
      // Best-effort refresh of cached user object so the avatar reflects immediately.
      try {
        const ev = new CustomEvent('profile-photo-updated', { detail: { url: res.data?.profile_photo_url } });
        window.dispatchEvent(ev);
      } catch (_e) { /* ignore */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not set profile photo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#03DFFC]"></div>
      </div>
    );
  }

  const { photos, count, role_name, requires_minimum, minimum_required } = data || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-portfolio-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button
            onClick={() => setShowUploadDialog(true)}
            className="bg-[#03DFFC] hover:bg-[#03DFFC] text-gray-900"
            disabled={count >= 20}
            data-testid="upload-photo-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Photo
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Portfolio Gallery</h1>
        <p className="text-gray-500 mb-6">
          {role_name ? `Showcase your best work as a ${role_name}.` : 'Showcase your best work photos.'}
          {' '}Clients review these when considering you for bookings.
        </p>

        {/* Requirements notice */}
        {requires_minimum && count < minimum_required && (
          <Alert className="mb-6 bg-amber-50 border-amber-300" data-testid="portfolio-requirement-notice">
            <Info className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>{role_name}</strong> profiles require a minimum of <strong>{minimum_required} portfolio photos</strong>.
              You have uploaded <strong>{count}</strong> so far. Please upload {minimum_required - count} more to complete your profile.
            </AlertDescription>
          </Alert>
        )}

        {/* Photo count */}
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="text-sm">
            {count}/20 photos
          </Badge>
          {requires_minimum && (
            <Badge className={count >= minimum_required ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
              {count >= minimum_required ? 'Requirement met' : `${minimum_required - count} more needed`}
            </Badge>
          )}
        </div>

        {/* Photo Grid */}
        {count === 0 ? (
          <Card className="bg-white border-gray-200 border-dashed border-2">
            <CardContent className="py-16 text-center">
              <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No portfolio photos yet</p>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-[#03DFFC] hover:bg-[#03DFFC] text-gray-900"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload Your First Photo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, idx) => (
              <div key={photo.id} className="group relative rounded-lg overflow-hidden bg-gray-100 aspect-square" data-testid="portfolio-photo">
                <img
                  src={photo.photo_url}
                  alt={photo.caption || 'Portfolio'}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setLightboxIdx(idx)}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  data-testid={`portfolio-photo-${idx}`}
                />
                {/* Iter-284d — Imported items (TrustedHerd / StaffConnect) carry
                    is_imported=true; show source badge and disable delete since
                    their bytes live in talent_media / Object Storage, not in
                    portfolio_photos. */}
                {photo.is_imported && (
                  <span
                    className="absolute top-1 left-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-white/90 text-gray-700 border border-gray-200"
                    data-testid={`portfolio-photo-imported-${idx}`}
                    title={`Imported from ${photo.source === 'trustedherd_import' ? 'TrustedHerd' : 'StaffConnect'}`}
                  >
                    {photo.source === 'trustedherd_import' ? 'TH' : 'SC'}
                  </span>
                )}
                <div className="absolute inset-0 bg-gray-50/0 group-hover:bg-gray-50/40 transition-all flex items-end pointer-events-none">
                  <div className="w-full p-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 justify-between pointer-events-auto">
                    {photo.caption && (
                      <span className="text-gray-900 text-xs truncate flex-1">{photo.caption}</span>
                    )}
                    {!photo.is_imported && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-7 px-2 ml-auto text-[11px]"
                        onClick={(e) => { e.stopPropagation(); handleSetAsProfile(photo.id); }}
                        data-testid={`set-profile-photo-btn-${idx}`}
                        title="Set as profile photo"
                      >
                        <UserCircle className="w-3 h-3 mr-1" /> Use as Profile
                      </Button>
                    )}
                    {!photo.is_imported && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                        data-testid="delete-photo-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Add More Button */}
            {count < 20 && (
              <div
                className="rounded-lg border-2 border-dashed border-gray-300 aspect-square flex items-center justify-center cursor-pointer hover:border-cyan-400 hover:bg-cyan-50 transition-colors"
                onClick={() => setShowUploadDialog(true)}
                data-testid="add-more-photos"
              >
                <div className="text-center">
                  <Plus className="w-8 h-8 text-gray-500 mx-auto mb-1" />
                  <span className="text-xs text-gray-500">Add Photo</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Role-specific tips */}
        <Card className="mt-8 bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-sm text-gray-700">Photo Tips for {role_name || 'Your Role'}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-gray-500 space-y-1">
              {(role_name === 'Brand Ambassador' || role_name === 'Model' || role_name === 'Influencer') && (
                <>
                  <li>- Professional headshots and full-body shots</li>
                  <li>- Photos from past brand activations or events</li>
                  <li>- Show your personality and professionalism</li>
                  <li>- High-quality, well-lit images perform best</li>
                </>
              )}
              {role_name === 'Bartender' && (
                <>
                  <li>- Photos of your cocktail creations and signature drinks</li>
                  <li>- Bar setup and presentation shots</li>
                  <li>- Action shots during service</li>
                  <li>- Flair bartending or mixology demonstrations</li>
                </>
              )}
              {(!role_name || !['Brand Ambassador', 'Model', 'Influencer', 'Bartender'].includes(role_name)) && (
                <>
                  <li>- Photos from past events you've worked</li>
                  <li>- Action shots showing you in your role</li>
                  <li>- Professional presentation and setup photos</li>
                  <li>- High-quality, well-lit images perform best</li>
                </>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Portfolio Photo</DialogTitle>
          <DialogDescription className="sr-only">Upload Portfolio Photo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Choose Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                }}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                disabled={uploading}
                data-testid="file-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Caption (optional)</label>
              <Input
                value={captionInput}
                onChange={(e) => setCaptionInput(e.target.value)}
                placeholder="Describe this photo..."
                data-testid="caption-input"
              />
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-cyan-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#03DFFC]"></div>
                Uploading...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
          data-testid="portfolio-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none"
            data-testid="lightbox-close-btn"
            aria-label="Close"
          >
            ×
          </button>
          {lightboxIdx > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center"
              data-testid="lightbox-prev-btn"
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          {lightboxIdx < photos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center"
              data-testid="lightbox-next-btn"
              aria-label="Next"
            >
              ›
            </button>
          )}
          <div
            className="max-w-6xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photos[lightboxIdx].photo_url}
              alt={photos[lightboxIdx].caption || 'Portfolio photo'}
              className="max-h-[85vh] max-w-full object-contain rounded-md"
              data-testid="lightbox-image"
            />
            {photos[lightboxIdx].caption && (
              <p className="text-white text-sm mt-3 text-center">{photos[lightboxIdx].caption}</p>
            )}
            <p className="text-white/50 text-xs mt-1">{lightboxIdx + 1} of {photos.length}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentPortfolio;
